import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Box, useBreakpointValue } from '@chakra-ui/react';
import BurrowSpinner from '../UI/BurrowSpinner';
import StorageManager from '../../utils/StorageManager';
import { openChatPanel } from '../../Redux/Chat/action';

// Critical routes - loaded immediately
import LandingPage from "../Landing/LandingPage";
import GatewayPage from "../Gateway/GatewayPage";
import Home from "../Home/Home";
import LegalDocRedirect from "../Legal/LegalDocRedirect";

// Lazy-loaded routes - split into separate chunks
const HomeDetails = lazy(() => import("../HomeDetails/HomeDetails").then(module => ({ default: module.HomeDetails })));
const VideoFinderPage = lazy(() => import("../Search/VideoFinderPage"));
const FilmsPage = lazy(() => import("../Films/FilmsPage"));
const FilmsDetails = lazy(() => import("../Films/FilmsDetails"));
const ShortsPage = lazy(() => import("../Shorts/ShortsPage"));
const ShortsDetails = lazy(() => import("../Shorts/ShortsDetails"));
const ClipsPage = lazy(() => import("../Clips/ClipsPage"));
const ClipsDetails = lazy(() => import("../Clips/ClipsDetails"));
const CategoriesPage = lazy(() => import("../Categories/CategoriesPage"));
const CategoryGridPage = lazy(() => import("../Categories/CategoryGridPage"));
const RegisterForm = lazy(() => import("../Auth/RegisterForm").then(module => ({ default: module.RegisterForm })));
const ForgotPassword = lazy(() => import("../Auth/ForgotPassword"));
const ResetPasswordFromEmail = lazy(() => import("../Auth/ResetPasswordFromEmail"));
const UserManagementPage = lazy(() => import("../Admin/UserManagementPage"));
const ViewQueuesPage = lazy(() => import("../ViewQueues/ViewQueuesPage"));
const QueueDetails = lazy(() => import("../ViewQueues/QueueDetails"));
const QueuePlayerPage = lazy(() => import("../ViewQueues/QueuePlayerPage"));
const FavoritesPage = lazy(() => import("../Favorites/FavoritesPage"));
const FavoritesPlayerPage = lazy(() => import("../Favorites/FavoritesPlayerPage"));
const WatchLaterPage = lazy(() => import('../WatchLater/WatchLaterPage'));
const WatchLaterPlayerPage = lazy(() => import('../WatchLater/WatchLaterPlayerPage'));
const VideoHistoryPage = lazy(() => import('../VideoHistory/VideoHistoryPage'));
const VideoHistoryPlayerPage = lazy(() => import('../VideoHistory/VideoHistoryPlayerPage'));
const AdminInboxPage = lazy(() => import('../Admin/AdminInboxPage'));
const UserTicketsPage = lazy(() => import('../User/UserTicketsPage'));
const StabilityMonitoringPage = lazy(() => import('../Admin/StabilityMonitoringPage'));
const StatusPage = lazy(() => import('../ValveSystem/StatusPage'));
const NotFoundPage = lazy(() => import('../ValveSystem/NotFoundPage'));
const BubChatContainer = lazy(() => import('../../components/BubChat/BubChatContainer'));
const CommentsDemo = lazy(() => import('../UI/CommentsDemo'));
const AdminHierarchyPage = lazy(() => import('../Admin/AdminHierarchyPage'));
const AdminMessagesPage = lazy(() => import('../Admin/AdminMessagesPage'));
const FeedPage = lazy(() => import('../Feed/FeedPage'));
const DashboardDen = lazy(() => import('../Dashboard/DashboardDen'));
const TutorialsPage = lazy(() => import('../Tutorials/TutorialsPage'));
const ChannelPage = lazy(() => import('../Feed/ChannelPage'));
const FullScreenPost = lazy(() => import('../Feed/FullScreenPost'));
const PostTypeHub = lazy(() => import('../Feed/PostTypeHub'));

// Loading fallback component
const RouteLoader = () => (
  <Box 
    display="flex" 
    alignItems="center" 
    justifyContent="center" 
    minH="100vh"
    bg="gray.50"
  >
    <BurrowSpinner size="xl" />
  </Box>
);

// Protected route component for admin-only routes
// requiredLevel: 'owner' | 'senior-admin' | 'admin' | 'moderator' (default: any admin)
// Phase 1C: Updated for sidecar model — checks isInAdminMode + legacy patterns
const ProtectedAdminRoute = ({ children, requiredLevel }) => {
  const isAdmin = useSelector(state => 
    state.auth?.isInAdminMode ||
    state.auth?.isAdmin ||
    state.auth?.role === 'admin' || 
    state.auth?.accountType === 'admin' ||
    state.auth?.user?.role === 'admin' || 
    state.auth?.user?.accountType === 'admin'
  );
  const adminLevel = useSelector(state => state.auth?.adminLevel || state.auth?.user?.adminLevel);
  const isAuthenticated = useSelector(state => Boolean(state.auth?.token));
  
  // Check StorageManager for more robust verification
  const token = StorageManager.get('burrowtoken');
  const localStorageUser = StorageManager.get('burrowUserData', null);
  
  const hasLocalStorageAuth = Boolean(token && localStorageUser);
  const localIsAdmin = localStorageUser?.accountType === 'admin' || localStorageUser?.role === 'admin' || localStorageUser?.hasAdminAccess;
  const effectiveLevel = adminLevel || localStorageUser?.adminLevel;
  
  // Level hierarchy check — Phase 1C: added senior-admin tier
  const meetsLevelRequirement = () => {
    if (!requiredLevel) return true; // No specific level required
    const hierarchy = { owner: 4, 'senior-admin': 3, admin: 2, moderator: 1 };
    return (hierarchy[effectiveLevel] || 0) >= (hierarchy[requiredLevel] || 0);
  };
  
  // Debug routing protection
  if (process.env.NODE_ENV === 'development') {
    console.log('Admin Route Protection Check:', {
      reduxIsAuthenticated: isAuthenticated,
      reduxIsAdmin: isAdmin,
      adminLevel: effectiveLevel,
      requiredLevel,
      hasLocalStorageAuth,
      localIsAdmin,
      route: window.location.pathname
    });
  }
  
  const isAuthorized = ((isAuthenticated && isAdmin) || (hasLocalStorageAuth && localIsAdmin)) && meetsLevelRequirement();
  return isAuthorized ? children : <Navigate to="/" replace state={{ authRedirect: true }} />;
};

// Protected route component for authenticated users
const ProtectedUserRoute = ({ children }) => {
  const isAuthenticated = useSelector(state => Boolean(state.auth?.token));
  const token = StorageManager.get('burrowtoken');
  
  // Debug routing protection
  if (process.env.NODE_ENV === 'development') {
    console.log('User Route Protection Check:', {
      reduxIsAuthenticated: isAuthenticated,
      hasLocalStorageToken: Boolean(token),
      route: window.location.pathname
    });
  }
  
  return isAuthenticated || Boolean(token) ? 
    children : <Navigate to="/" replace state={{ authRedirect: true }} />;
};

// Redirect old /details/:id/:slug URLs to clean /:id/:slug format
const DetailsRedirect = () => {
  const { id, slug } = useParams();
  return <Navigate to={slug ? `/${id}/${slug}` : `/${id}/video`} replace />;
};

/**
 * ChatRoute — smart handler for the /chat path.
 * Desktop (md+): opens the floating ChatPanel and redirects back to /home.
 * Mobile:        renders BubChatContainer full-screen as before.
 */
const ChatRoute = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isDesktop = useBreakpointValue({ base: false, md: true });

  useEffect(() => {
    if (isDesktop === true) {
      dispatch(openChatPanel());
      navigate('/home', { replace: true });
    }
  }, [isDesktop, dispatch, navigate]);

  // On mobile render full-page chat; on desktop render nothing (redirect fires in useEffect)
  if (isDesktop) return null;
  return (
    <Suspense fallback={<RouteLoader />}>
      <BubChatContainer />
    </Suspense>
  );
};

const AllRoutes = () => {

  return (
    <Suspense fallback={<RouteLoader />}>
      <Routes>
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route path="/gateway" element={<Navigate to="/home" replace />} />
        {/* Full legal documents (the real ToS/Privacy used by the latest Burrow Lite) —
            served as the original standalone HTML docs from public/legal/ */}
        <Route path="/terms" element={<LegalDocRedirect file="/legal/BurrowTv-Terms-of-Service.html" />} />
        <Route path="/privacy" element={<LegalDocRedirect file="/legal/BurrowTv-Privacy-Policy.html" />} />
        {/* Make Home publicly accessible so the gateway Enter button can navigate here without auth */}
        <Route path="/home" element={<Home />} />
        {/* Valve system member door (FINAL-status-page.html) — public, never gated */}
        <Route path="/status" element={<StatusPage />} />
      {/* Legacy Edit Mode routes (removed 9-3-26) — old bookmarks land on home */}
      <Route path="/edit/*" element={<Navigate to="/" replace />} />
      {/* Mocks are design artifacts only — stop any /mocks/* url (static removed; guard SPA fallback) */}
      <Route path="/mocks/*" element={<Navigate to="/" replace />} />
      <Route path="/details/:id" element={<DetailsRedirect />} />
      <Route path="/details/:id/:slug" element={<DetailsRedirect />} />
      <Route path="/finder" element={<VideoFinderPage />} />
      <Route path="/films" element={<FilmsPage />} />
      <Route path="/films/:id" element={<FilmsDetails />} />
      <Route path="/films/:id/:slug" element={<FilmsDetails />} />
      <Route path="/shorts" element={<ShortsPage />} />
      <Route path="/shorts/:id" element={<ShortsDetails />} />
      <Route path="/shorts/:id/:slug" element={<ShortsDetails />} />
      <Route path="/clips" element={<ClipsPage />} />
      <Route path="/clips/:id" element={<ClipsDetails />} />
      <Route path="/clips/:id/:slug" element={<ClipsDetails />} />
      <Route path="/categories" element={<CategoriesPage />} />
      <Route path="/categories/:tagName" element={<CategoryGridPage />} />
      <Route path="/categories/:tagName/:slug" element={<CategoryGridPage />} />
      <Route path="/signup" element={<RegisterForm />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPasswordFromEmail />} />
      <Route path="/admin/users" element={<ProtectedAdminRoute><UserManagementPage /></ProtectedAdminRoute>} />
      <Route path="/view-queues" element={<ViewQueuesPage />} />
      <Route path="/view-queues/:queueId" element={<QueueDetails />} />
      <Route path="/view-queues-watch/:queueId" element={<QueuePlayerPage />} />
      <Route path="/favorites" element={<FavoritesPage />} />
      <Route path="/favorites-player" element={<FavoritesPlayerPage />} />
      <Route path="/watch-later" element={<WatchLaterPage />} />
      <Route path="/watch-later-player" element={<WatchLaterPlayerPage />} />
      <Route path="/video-history" element={<VideoHistoryPage />} />
      <Route path="/video-history-player" element={<VideoHistoryPlayerPage />} />
      
      {/* Protected ticket routes */}
      <Route path="/admin/inbox" element={<ProtectedAdminRoute><AdminInboxPage /></ProtectedAdminRoute>} />
      <Route path="/tickets" element={<ProtectedUserRoute><UserTicketsPage /></ProtectedUserRoute>} />
      
      {/* Protected stability monitoring route */}
      <Route path="/admin/stability" element={<ProtectedAdminRoute><StabilityMonitoringPage /></ProtectedAdminRoute>} />
      
      {/* Admin messaging */}
      <Route path="/admin/messages" element={<ProtectedAdminRoute><AdminMessagesPage /></ProtectedAdminRoute>} />

      {/* Owner-only: Admin team management */}
      <Route path="/admin/manage-admins" element={<ProtectedAdminRoute requiredLevel="owner"><AdminHierarchyPage /></ProtectedAdminRoute>} />
      
      {/* Dashboard (The Den) — protected; the member lander */}
      <Route path="/dashboard" element={<ProtectedUserRoute><DashboardDen /></ProtectedUserRoute>} />
      <Route path="/tutorials" element={<ProtectedUserRoute><TutorialsPage /></ProtectedUserRoute>} />

      {/* Feed route — protected, authenticated users only */}
      <Route path="/feed" element={<ProtectedUserRoute><FeedPage /></ProtectedUserRoute>} />
      <Route path="/channel/:username" element={<ProtectedUserRoute><ChannelPage /></ProtectedUserRoute>} />
      <Route path="/post/:postId" element={<ProtectedUserRoute><FullScreenPost /></ProtectedUserRoute>} />
      <Route path="/hub/:postType" element={<ProtectedUserRoute><PostTypeHub /></ProtectedUserRoute>} />

      {/* Chat route:
           - Mobile: full-page BubChatContainer (immersive, no panel)
           - Desktop: open the floating ChatPanel and redirect to /home */}
      <Route path="/chat" element={<ChatRoute />} />
      
      {/* Comments Demo Route */}
      <Route path="/comments-demo" element={<CommentsDemo />} />
      
      {/* Clean video detail URLs — /:id/:slug (must be LAST before catch-all) */}
      <Route path="/:id/:slug" element={<HomeDetails />} />
      <Route path="/:id" element={<HomeDetails />} />
      
      {/* Wrong turn — calm catch-all in the same warm family as the /status door */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
};

export default AllRoutes;